import React, { useEffect, useMemo, useState } from "react";
import api, { formatError, API_BASE } from "@/lib/api";
import { PageHeader, StatCard, LoadingState, Chip } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DownloadSimple, Printer, FileText, PencilSimple, TrendUp, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const inrFull = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

// ---------- Financial year helpers (India: Apr-Mar) ----------
function currentFYStartYear() {
    const t = new Date();
    return t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
}
function fyLabel(startYear) {
    return `${startYear}-${String(startYear + 1).slice(-2)}`;
}
function fyBounds(startYear) {
    return { from: `${startYear}-04-01`, to: `${startYear + 1}-03-31` };
}
function fyOptions() {
    const cur = currentFYStartYear();
    const opts = [];
    for (let y = cur - 3; y <= cur + 1; y += 1) opts.push({ value: fyLabel(y), startYear: y });
    return opts.reverse();
}

// ---------- Period presets ----------
const PERIOD_TYPES = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "half_yearly", label: "Half-Yearly" },
    { value: "annual", label: "Annual" },
    { value: "custom", label: "Custom" },
];

const MONTHS = [
    { v: "04", n: "April" }, { v: "05", n: "May" }, { v: "06", n: "June" },
    { v: "07", n: "July" }, { v: "08", n: "August" }, { v: "09", n: "September" },
    { v: "10", n: "October" }, { v: "11", n: "November" }, { v: "12", n: "December" },
    { v: "01", n: "January" }, { v: "02", n: "February" }, { v: "03", n: "March" },
];

const QUARTERS = [
    { v: "Q1", label: "Q1 (Apr–Jun)" },
    { v: "Q2", label: "Q2 (Jul–Sep)" },
    { v: "Q3", label: "Q3 (Oct–Dec)" },
    { v: "Q4", label: "Q4 (Jan–Mar)" },
];

const HALVES = [
    { v: "H1", label: "H1 (Apr–Sep)" },
    { v: "H2", label: "H2 (Oct–Mar)" },
];

function lastDayOfMonth(year, monthIdx0) {
    return new Date(year, monthIdx0 + 1, 0).getDate();
}

function computePeriod(periodType, fyStartYear, opts) {
    const { month, quarter, half, customFrom, customTo } = opts || {};
    if (periodType === "annual") return fyBounds(fyStartYear);
    if (periodType === "custom") {
        return {
            from: customFrom || fyBounds(fyStartYear).from,
            to: customTo || fyBounds(fyStartYear).to,
        };
    }
    if (periodType === "monthly") {
        const m = month || "04";
        const year = ["01", "02", "03"].includes(m) ? fyStartYear + 1 : fyStartYear;
        const idx = parseInt(m, 10) - 1;
        const last = lastDayOfMonth(year, idx);
        return { from: `${year}-${m}-01`, to: `${year}-${m}-${String(last).padStart(2, "0")}` };
    }
    if (periodType === "quarterly") {
        const q = quarter || "Q1";
        const map = {
            Q1: [`${fyStartYear}-04-01`, `${fyStartYear}-06-30`],
            Q2: [`${fyStartYear}-07-01`, `${fyStartYear}-09-30`],
            Q3: [`${fyStartYear}-10-01`, `${fyStartYear}-12-31`],
            Q4: [`${fyStartYear + 1}-01-01`, `${fyStartYear + 1}-03-31`],
        };
        return { from: map[q][0], to: map[q][1] };
    }
    if (periodType === "half_yearly") {
        const h = half || "H1";
        return h === "H1"
            ? { from: `${fyStartYear}-04-01`, to: `${fyStartYear}-09-30` }
            : { from: `${fyStartYear}-10-01`, to: `${fyStartYear + 1}-03-31` };
    }
    return fyBounds(fyStartYear);
}

function periodLabel(periodType, fyStartYear, opts) {
    const { month, quarter, half } = opts || {};
    if (periodType === "annual") return `Annual · FY ${fyLabel(fyStartYear)}`;
    if (periodType === "custom") return `Custom Range`;
    if (periodType === "monthly") {
        const name = MONTHS.find(m => m.v === (month || "04"))?.n || "April";
        const year = ["01", "02", "03"].includes(month || "04") ? fyStartYear + 1 : fyStartYear;
        return `Monthly · ${name} ${year}`;
    }
    if (periodType === "quarterly") return `Quarterly · ${QUARTERS.find(q => q.v === (quarter || "Q1"))?.label}`;
    if (periodType === "half_yearly") return `Half-Yearly · ${HALVES.find(h => h.v === (half || "H1"))?.label}`;
    return "Report";
}

// ---------- Sections ----------

function ExecutiveSummary({ ex }) {
    const positive = ex.net_position >= 0;
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="fr-exec-summary">
            <StatCard label="Maintenance Billed" value={inr(ex.total_billed)} hint="During selected period" />
            <StatCard label="Total Collected" value={inr(ex.total_collected)} hint="Confirmed payments only" />
            <StatCard label="Total Outstanding" value={inr(ex.total_outstanding)} hint="As of today" accent />
            <StatCard label="Total Expenses" value={inr(ex.total_expenses)} hint="Excludes voided" />
            <StatCard label="Net Position" value={inr(ex.net_position)} hint={positive ? "Surplus" : "Deficit"} accent={!positive} />
            <StatCard label="Collection Efficiency" value={`${ex.collection_efficiency_pct}%`} hint="Collected ÷ Billed" />
        </div>
    );
}

function Section({ title, num, children, extra }) {
    return (
        <section className="bg-white border border-brand-line rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-brand-ink text-white text-xs font-bold flex items-center justify-center">{num}</span>
                    <h3 className="font-heading text-lg tracking-tight text-brand-ink">{title}</h3>
                </div>
                {extra}
            </div>
            {children}
        </section>
    );
}

function KVRow({ k, v, bold, dashed }) {
    return (
        <div className={`flex items-center justify-between py-2 ${dashed ? "border-b border-dashed border-brand-sage/60" : "border-b border-brand-line/60"}`}>
            <span className={`text-sm ${bold ? "font-semibold text-brand-ink" : "text-brand-inkSoft"}`}>{k}</span>
            <span className={`font-heading tabular-nums ${bold ? "text-brand-ink text-lg" : "text-brand-ink"}`}>{v}</span>
        </div>
    );
}

// ---------- Main Component ----------
export default function FinancialReports() {
    const [periodType, setPeriodType] = useState("annual");
    const [fyStartYear, setFyStartYear] = useState(currentFYStartYear());
    const [month, setMonth] = useState("04");
    const [quarter, setQuarter] = useState("Q1");
    const [half, setHalf] = useState("H1");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openingDialog, setOpeningDialog] = useState(false);
    const [openingBalances, setOpeningBalances] = useState([]);
    const [obForm, setObForm] = useState({ fy: "", amount: "", date: "", note: "" });

    const derived = useMemo(() => {
        const p = computePeriod(periodType, fyStartYear, { month, quarter, half, customFrom, customTo });
        const label = periodLabel(periodType, fyStartYear, { month, quarter, half });
        return { ...p, label };
    }, [periodType, fyStartYear, month, quarter, half, customFrom, customTo]);

    async function loadReport() {
        setLoading(true);
        try {
            const { data } = await api.get("/financial-reports", {
                params: {
                    date_from: derived.from,
                    date_to: derived.to,
                    fy: fyLabel(fyStartYear),
                },
            });
            setReport(data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    async function loadOpeningBalances() {
        try {
            const { data } = await api.get("/financial-reports/opening-balance");
            setOpeningBalances(data);
        } catch (e) { /* ignore */ }
    }

    useEffect(() => { loadReport(); /* eslint-disable-next-line */ }, [derived.from, derived.to, fyStartYear]);
    useEffect(() => { loadOpeningBalances(); }, []);

    function openOpeningDialog() {
        const existing = openingBalances.find(o => o.fy === fyLabel(fyStartYear));
        setObForm({
            fy: fyLabel(fyStartYear),
            amount: existing ? String(existing.amount) : "",
            date: existing?.date || `${fyStartYear}-04-01`,
            note: existing?.note || "",
        });
        setOpeningDialog(true);
    }

    async function saveOpeningBalance() {
        try {
            await api.post("/financial-reports/opening-balance", {
                fy: obForm.fy,
                amount: parseFloat(obForm.amount || "0"),
                date: obForm.date,
                note: obForm.note || null,
            });
            toast.success("Opening balance saved");
            setOpeningDialog(false);
            await loadOpeningBalances();
            await loadReport();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    function downloadPDF() {
        const token = localStorage.getItem("maintayn_token");
        const params = new URLSearchParams({
            date_from: derived.from,
            date_to: derived.to,
            fy: fyLabel(fyStartYear),
            period_label: derived.label,
            auth: token || "",
        });
        const url = `${API_BASE}/financial-reports/pdf?${params.toString()}`;
        // Use fetch to include auth header cleanly, then blob download
        (async () => {
            try {
                const res = await api.get(`/financial-reports/pdf`, {
                    params: {
                        date_from: derived.from,
                        date_to: derived.to,
                        fy: fyLabel(fyStartYear),
                        period_label: derived.label,
                    },
                    responseType: "blob",
                });
                const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = `financial-report-${derived.from}-to-${derived.to}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                toast.success("PDF downloaded");
            } catch (e) {
                toast.error(formatError(e));
            }
        })();
    }

    function printReport() {
        window.print();
    }

    return (
        <div className="animate-fade-up space-y-6">
            <PageHeader
                overline="Finance · Reporting"
                title="Financial Reports"
                description="Consolidated society financial statement — derived from existing Maintenance, Payments and Expenses records."
                actions={
                    <div className="flex flex-wrap gap-2 print:hidden">
                        <Button
                            data-testid="fr-opening-balance-btn"
                            variant="outline"
                            onClick={openOpeningDialog}
                            className="rounded-full border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white"
                        >
                            <PencilSimple size={16} className="mr-1.5" /> Opening Balance
                        </Button>
                        <Button
                            data-testid="fr-print-btn"
                            variant="outline"
                            onClick={printReport}
                            className="rounded-full border-brand-line"
                        >
                            <Printer size={16} className="mr-1.5" /> Print
                        </Button>
                        <Button
                            data-testid="fr-download-pdf-btn"
                            onClick={downloadPDF}
                            className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white"
                        >
                            <DownloadSimple size={16} className="mr-1.5" /> Download PDF
                        </Button>
                    </div>
                }
            />

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-5 print:hidden" data-testid="fr-filters">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-overline text-brand-inkSoft">Period Type</Label>
                        <Select value={periodType} onValueChange={setPeriodType}>
                            <SelectTrigger data-testid="fr-period-type" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                            <SelectContent>{PERIOD_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-overline text-brand-inkSoft">Financial Year</Label>
                        <Select value={String(fyStartYear)} onValueChange={(v) => setFyStartYear(parseInt(v, 10))}>
                            <SelectTrigger data-testid="fr-fy-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                            <SelectContent>{fyOptions().map(o => <SelectItem key={o.value} value={String(o.startYear)}>FY {o.value}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    {periodType === "monthly" && (
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-overline text-brand-inkSoft">Month</Label>
                            <Select value={month} onValueChange={setMonth}>
                                <SelectTrigger data-testid="fr-month-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{MONTHS.map(m => <SelectItem key={m.v} value={m.v}>{m.n}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                    {periodType === "quarterly" && (
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-overline text-brand-inkSoft">Quarter</Label>
                            <Select value={quarter} onValueChange={setQuarter}>
                                <SelectTrigger data-testid="fr-quarter-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{QUARTERS.map(q => <SelectItem key={q.v} value={q.v}>{q.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                    {periodType === "half_yearly" && (
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-overline text-brand-inkSoft">Half</Label>
                            <Select value={half} onValueChange={setHalf}>
                                <SelectTrigger data-testid="fr-half-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{HALVES.map(h => <SelectItem key={h.v} value={h.v}>{h.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                    {periodType === "custom" && (
                        <>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-overline text-brand-inkSoft">From</Label>
                                <Input data-testid="fr-custom-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-overline text-brand-inkSoft">To</Label>
                                <Input data-testid="fr-custom-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="rounded-sm border-brand-line" />
                            </div>
                        </>
                    )}
                </div>
                <div className="mt-3 text-xs text-brand-inkSoft" data-testid="fr-period-range">
                    Selected: <span className="font-medium text-brand-ink">{derived.label}</span> · {derived.from} → {derived.to}
                </div>
            </div>

            {loading && <LoadingState message="Building report..." />}

            {report && !loading && (
                <div id="fr-print-area" className="space-y-6" data-testid="fr-report">
                    {/* Report header (visible in print) */}
                    <div className="bg-brand-ink text-white rounded-sm p-6 print:bg-white print:text-brand-ink print:border print:border-brand-ink">
                        <div className="flex items-start justify-between flex-wrap gap-3">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.3em] text-brand-sage print:text-brand-inkSoft">Society Financial Statement</p>
                                <h1 className="font-heading text-2xl md:text-3xl tracking-tight mt-1" data-testid="fr-society-name">{report.society.name}</h1>
                                <p className="text-sm text-brand-sage mt-1 print:text-brand-inkSoft">
                                    {[report.society.address, report.society.city, report.society.state, report.society.pincode].filter(Boolean).join(", ")}
                                </p>
                            </div>
                            <div className="text-right text-sm">
                                <p><span className="text-brand-sage print:text-brand-inkSoft">Period:</span> {report.period.from} → {report.period.to}</p>
                                <p><span className="text-brand-sage print:text-brand-inkSoft">FY:</span> {report.financial_year}</p>
                                <p><span className="text-brand-sage print:text-brand-inkSoft">Generated:</span> {report.generated_at.slice(0, 10)}</p>
                            </div>
                        </div>
                    </div>

                    {/* 2. Executive Summary */}
                    <Section num="1" title="Executive Summary">
                        <ExecutiveSummary ex={report.executive_summary} />
                    </Section>

                    {/* 3. Maintenance Collection */}
                    <Section num="2" title="Maintenance Collection" extra={
                        <Chip variant={report.maintenance_collection.collection_efficiency_pct >= 80 ? "success" : "warn"}>
                            {report.maintenance_collection.collection_efficiency_pct}% efficiency
                        </Chip>
                    }>
                        <div className="grid md:grid-cols-2 gap-x-8">
                            <div>
                                <KVRow k="Total maintenance billed" v={inrFull(report.maintenance_collection.billed)} />
                                <KVRow k="Total collected" v={inrFull(report.maintenance_collection.collected)} />
                                <KVRow k="Outstanding (period)" v={inrFull(report.maintenance_collection.outstanding_in_period)} bold />
                            </div>
                            <div>
                                <KVRow k="Invoices generated" v={report.maintenance_collection.invoices_generated} />
                                <KVRow k="Fully paid" v={report.maintenance_collection.invoices_fully_paid} />
                                <KVRow k="Partially paid" v={report.maintenance_collection.invoices_partial} />
                                <KVRow k="Unpaid" v={report.maintenance_collection.invoices_unpaid} />
                            </div>
                        </div>
                    </Section>

                    {/* 4. Payment Mode Summary */}
                    <Section num="3" title="Payment Mode Summary">
                        <div className="overflow-x-auto" data-testid="fr-payment-modes">
                            <table className="w-full text-sm min-w-[400px]">
                                <thead>
                                    <tr className="bg-brand-bg border-b border-brand-line text-left">
                                        <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Mode</th>
                                        <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline text-right">Txns</th>
                                        <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.payment_modes.filter(p => p.count > 0 || p.amount > 0).map(p => (
                                        <tr key={p.mode} className="border-b border-brand-line/60" data-testid={`fr-mode-${p.mode}`}>
                                            <td className="py-2 px-4 text-brand-ink">{p.label}</td>
                                            <td className="py-2 px-4 text-brand-inkSoft text-right tabular-nums">{p.count}</td>
                                            <td className="py-2 px-4 text-brand-ink font-heading text-right tabular-nums">{inrFull(p.amount)}</td>
                                        </tr>
                                    ))}
                                    {report.payment_modes.every(p => p.count === 0) && (
                                        <tr><td colSpan={3} className="py-4 px-4 text-center text-brand-inkSoft italic text-xs">No payments in this period.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Section>

                    {/* 5. Expense Summary */}
                    <Section num="4" title="Expense Summary">
                        <div className="mb-4">
                            <KVRow k="Total expenses" v={inrFull(report.expense_summary.total_expenses)} bold />
                            <KVRow k="Number of entries" v={report.expense_summary.expense_count} />
                        </div>
                        <div className="overflow-x-auto" data-testid="fr-expense-categories">
                            <table className="w-full text-sm min-w-[400px]">
                                <thead>
                                    <tr className="bg-brand-bg border-b border-brand-line text-left">
                                        <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Category</th>
                                        <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline text-right">Count</th>
                                        <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.expense_summary.categories.map(c => (
                                        <tr key={c.category} className="border-b border-brand-line/60">
                                            <td className="py-2 px-4 text-brand-ink">{c.category}</td>
                                            <td className="py-2 px-4 text-brand-inkSoft text-right tabular-nums">{c.count}</td>
                                            <td className="py-2 px-4 text-brand-ink font-heading text-right tabular-nums">{inrFull(c.amount)}</td>
                                        </tr>
                                    ))}
                                    {report.expense_summary.categories.length === 0 && (
                                        <tr><td colSpan={3} className="py-4 px-4 text-center text-brand-inkSoft italic text-xs">No expenses in this period.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Section>

                    {/* 6. Outstanding / Receivables */}
                    <Section num="5" title="Outstanding & Receivables (as of today)">
                        <div className="grid md:grid-cols-3 gap-3 mb-4" data-testid="fr-outstanding-summary">
                            <StatCard label="Total Outstanding" value={inr(report.outstanding.total_outstanding)} accent />
                            <StatCard label="Flats with balance" value={report.outstanding.flats_with_outstanding} />
                            <StatCard label="Defaulters (>90 days)" value={report.outstanding.defaulter_count} hint={report.outstanding.defaulter_rule} accent />
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[400px]">
                                <thead>
                                    <tr className="bg-brand-bg border-b border-brand-line text-left">
                                        <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Aging Bucket</th>
                                        <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline text-right">Invoices</th>
                                        <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(report.outstanding.aging).map(([bucket, v]) => (
                                        <tr key={bucket} className="border-b border-brand-line/60" data-testid={`fr-aging-${bucket}`}>
                                            <td className="py-2 px-4 text-brand-ink">
                                                {bucket === "current" ? "Current (not due)" :
                                                    bucket === "1-30" ? "1–30 days overdue" :
                                                        bucket === "31-60" ? "31–60 days overdue" :
                                                            bucket === "61-90" ? "61–90 days overdue" :
                                                                "90+ days overdue"}
                                            </td>
                                            <td className="py-2 px-4 text-brand-inkSoft text-right tabular-nums">{v.count}</td>
                                            <td className="py-2 px-4 text-brand-ink font-heading text-right tabular-nums">{inrFull(v.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>

                    {/* 7. Maintenance Tally */}
                    <Section num="6" title="Maintenance Tally">
                        <div className="max-w-md mx-auto" data-testid="fr-tally">
                            <KVRow k="Maintenance Demand" v={inrFull(report.maintenance_tally.demand)} dashed />
                            <KVRow k="Less: Amount Collected" v={`− ${inrFull(report.maintenance_tally.collected)}`} dashed />
                            <div className="mt-2 flex items-center justify-between bg-brand-ink text-white px-4 py-3 rounded-sm">
                                <span className="font-medium text-sm uppercase tracking-wide">Outstanding</span>
                                <span className="font-heading text-xl tabular-nums">{inrFull(report.maintenance_tally.outstanding)}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6 text-center">
                            <div><p className="text-xs text-brand-inkSoft uppercase tracking-overline">Total Flats</p><p className="font-heading text-xl text-brand-ink mt-1">{report.maintenance_tally.total_flats}</p></div>
                            <div><p className="text-xs text-brand-inkSoft uppercase tracking-overline">Fully Paid</p><p className="font-heading text-xl text-brand-ink mt-1">{report.maintenance_tally.fully_paid_flats}</p></div>
                            <div><p className="text-xs text-brand-inkSoft uppercase tracking-overline">Partial</p><p className="font-heading text-xl text-brand-ink mt-1">{report.maintenance_tally.partial_flats}</p></div>
                            <div><p className="text-xs text-brand-inkSoft uppercase tracking-overline">Unpaid</p><p className="font-heading text-xl text-brand-action mt-1">{report.maintenance_tally.unpaid_flats}</p></div>
                            <div><p className="text-xs text-brand-inkSoft uppercase tracking-overline">Efficiency</p><p className="font-heading text-xl text-brand-ink mt-1">{report.maintenance_tally.collection_efficiency_pct}%</p></div>
                        </div>
                    </Section>

                    {/* 8. Financial Position */}
                    <Section num="7" title="Financial Position" extra={
                        !report.financial_position.opening_balance_set && (
                            <Chip variant="warn">Opening balance not set</Chip>
                        )
                    }>
                        <div className="max-w-lg mx-auto" data-testid="fr-position">
                            {report.financial_position.opening_balance_set ? (
                                <KVRow
                                    k={`Opening Balance (${report.financial_position.opening_balance_date})`}
                                    v={inrFull(report.financial_position.opening_balance)}
                                    dashed
                                />
                            ) : (
                                <div className="py-2 border-b border-dashed border-brand-sage/60 flex items-center justify-between">
                                    <span className="text-sm text-brand-inkSoft italic">Opening Balance not configured for FY {report.financial_year}</span>
                                    <Button size="sm" variant="ghost" onClick={openOpeningDialog} className="text-brand-action hover:bg-brand-action/10 print:hidden">
                                        <PencilSimple size={14} className="mr-1" /> Set
                                    </Button>
                                </div>
                            )}
                            <KVRow k="+ Income / Collections" v={inrFull(report.financial_position.income_collections)} dashed />
                            <KVRow k="− Expenses" v={`− ${inrFull(report.financial_position.expenses)}`} dashed />
                            <div className={`mt-3 flex items-center justify-between px-4 py-3 rounded-sm ${report.financial_position.closing_position >= 0 ? "bg-brand-ink text-white" : "bg-brand-action text-white"}`}>
                                <span className="font-medium text-sm uppercase tracking-wide">Closing Position</span>
                                <span className="font-heading text-xl tabular-nums">{inrFull(report.financial_position.closing_position)}</span>
                            </div>
                            {report.financial_position.opening_balance_note && (
                                <p className="text-xs text-brand-inkSoft italic mt-2">Note: {report.financial_position.opening_balance_note}</p>
                            )}
                        </div>
                    </Section>

                    {/* 9. Income Summary */}
                    <Section num="8" title="Income Summary">
                        <div className="max-w-lg" data-testid="fr-income">
                            <KVRow k="Maintenance collections" v={inrFull(report.income_summary.maintenance_collections)} dashed />
                            {report.income_summary.amenity_income > 0 && (
                                <KVRow k="Amenity income" v={inrFull(report.income_summary.amenity_income)} dashed />
                            )}
                            {report.income_summary.work_order_income > 0 && (
                                <KVRow k="Work Order income" v={inrFull(report.income_summary.work_order_income)} dashed />
                            )}
                            {report.income_summary.other_income > 0 && (
                                <KVRow k="Other income" v={inrFull(report.income_summary.other_income)} dashed />
                            )}
                            <KVRow k="Total Income" v={inrFull(report.income_summary.total_income)} bold />
                            <p className="text-xs text-brand-inkSoft italic mt-3">{report.income_summary.note}</p>
                        </div>
                    </Section>

                    {/* Footer */}
                    <div className="text-center pt-4 text-xs text-brand-inkSoft">
                        <p>Generated through <span className="font-semibold text-brand-ink">Maintayn — Community OS</span> · {report.generated_at.slice(0, 10)}</p>
                        <p className="mt-1">This is a system-generated society financial statement. Not a statutory accounting balance sheet.</p>
                    </div>
                </div>
            )}

            {/* Opening Balance Dialog */}
            <Dialog open={openingDialog} onOpenChange={setOpeningDialog}>
                <DialogContent className="rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading tracking-tight text-xl">Set Opening Balance</DialogTitle>
                        <DialogDescription>
                            Configure the financial position at the start of a Financial Year. This is only used in the "Financial Position" section.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Financial Year</Label>
                            <Select value={obForm.fy} onValueChange={(v) => {
                                const startYear = parseInt(v.split("-")[0], 10);
                                setObForm({ ...obForm, fy: v, date: `${startYear}-04-01` });
                            }}>
                                <SelectTrigger data-testid="fr-ob-fy" className="rounded-sm border-brand-line"><SelectValue placeholder="Select FY" /></SelectTrigger>
                                <SelectContent>{fyOptions().map(o => <SelectItem key={o.value} value={o.value}>FY {o.value}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Opening Balance Amount (₹)</Label>
                            <Input data-testid="fr-ob-amount" type="number" step="0.01" value={obForm.amount} onChange={(e) => setObForm({ ...obForm, amount: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Opening Balance Date</Label>
                            <Input data-testid="fr-ob-date" type="date" value={obForm.date} onChange={(e) => setObForm({ ...obForm, date: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Note (optional)</Label>
                            <Textarea data-testid="fr-ob-note" rows={2} value={obForm.note} onChange={(e) => setObForm({ ...obForm, note: e.target.value })} placeholder="e.g. Carried forward from previous FY" className="rounded-sm border-brand-line" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpeningDialog(false)} className="rounded-full">Cancel</Button>
                        <Button data-testid="fr-ob-save" onClick={saveOpeningBalance} className="rounded-full bg-brand-action hover:bg-brand-actionHover">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Print styles */}
            <style>{`
                @media print {
                    body { background: white !important; }
                    .print\\:hidden { display: none !important; }
                    #fr-print-area { break-inside: avoid; }
                    section { break-inside: avoid; margin-bottom: 16px !important; }
                    aside, nav, header { display: none !important; }
                }
            `}</style>
        </div>
    );
}
